<script setup>
import { computed } from 'vue';
import { isbot } from 'isbot';
import UtilityService from '../services/UtilityService';
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();
const props = defineProps({
    staffList: Object,
});

const isCrawler = computed(() => {
    return isbot(navigator.userAgent);
});
const showNativeTitle = computed(() => {
    return store.state.showNativeTitle == '1';
});

const statusText = computed(() => {
    return props.staffList.status ? t(`common.statusWithBadge.${props.staffList.status}`) : '';
});
</script>

<template>
    <RouterLink class="flex flex-col bg-app-surface0 rounded-md shadow-sm overflow-hidden transition-transform duration-[30ms] hover:scale-[102%] focus:scale-[102%] outline-none" :to="`/staff/${staffList.slug || staffList.uuid}`">
        <div class="bg-keyframe text-white px-3 py-2 font-bold">
            <p class="text-xs truncate" v-if="showNativeTitle && staffList.native && staffList.native !== staffList.title">{{ staffList.native }}</p>
            <p class="truncate">{{ staffList.title }}</p>
        </div>
        <div class="flex flex-1">
            <div
                v-if="staffList.coverImage && !(staffList.isAdult == 1 && isCrawler)"
                class="bg-app-surface1 bg-cover bg-center w-[100px]"
                :class="{ 'nsfw': staffList.isAdult == 1 }"
                :style="{ backgroundImage: `url('${staffList.coverImage}')` }"
            ></div>
            <div class="relative content flex-1 overflow-hidden">
                <div class="h-full flex flex-col px-3 py-2 text-sm min-h-[140px]">
                    <i18n-t keypath="staffListEmbed.status" tag="p" v-if="staffList.status" :title="t('staffListEmbed.status', { status: statusText })" class="truncate">
                        <template #status>
                            <strong>{{ statusText }}</strong>
                        </template>
                    </i18n-t>
                    <i18n-t keypath="staffListEmbed.release" tag="p" :title="t('staffListEmbed.release', { release: staffList.release })" class="truncate">
                        <template #release>
                            <strong>{{ staffList.release }}</strong>
                        </template>
                    </i18n-t>
                    <i18n-t v-if="staffList.studios.length > 0" keypath="staffListEmbed.studio" tag="p" :title="t('staffListEmbed.studio', { studio: staffList.studios.join(', ') })" class="truncate">
                        <template #studio>
                            <strong>{{ staffList.studios.join(', ') }}</strong>
                        </template>
                    </i18n-t>
                    <i18n-t keypath="staffListEmbed.episodes" tag="p" :title="t('staffListEmbed.episodes', { episodes: staffList.episodes || 1 })" class="truncate">
                        <template #episodes>
                            <strong>{{ staffList.episodes || 1 }}</strong>
                        </template>
                    </i18n-t>
                    <p v-if="staffList.contributors && staffList.contributors.length > 0">
                        <i18n-t keypath="staffListEmbed.staffListBy">
                            <template #authors>
                                <a
                                    v-for="contributor of staffList.contributors"
                                    v-show="!contributor.anonymous || contributor.level == 0"
                                    :href="!contributor.anonymous ? `/user/${contributor.id}` : undefined"
                                    target="_blank"
                                    :tabindex="contributor.anonymous ? -1 : undefined"
                                    :class="[
                                        'inline-flex items-center justify-center bg-app-surface3 text-app-text rounded-full px-1.5 py-0.5 text-xs font-semibold mr-0.5',
                                        !contributor.anonymous && 'hover:bg-keyframe hover:text-white transition-colors duration-75'
                                    ]"
                                    @click.stop
                                >
                                    {{ contributor.screen_name }}
                                </a>
                            </template>
                        </i18n-t>
                    </p>
                    <a v-if="staffList.anilistId" class="absolute flex items-end justify-end right-0 bottom-0 p-2" @click.stop :href="`https://anilist.co/anime/${staffList.anilistId}`" target="_blank">
                        <img src="@/assets/img/anilist.svg" alt="Anilist Logo" class="inline h-5 w-5" style="filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.3))" />
                    </a>
                    <p class="mt-1 flex-1 flex flex-col justify-end text-gray-400 text-xs truncate">
                        <i18n-t keypath="staffListEmbed.lastModified" tag="span" class="flex flex-col sm:inline">
                            <template #date>
                                <strong>{{ UtilityService.getDateTime(staffList.lastModified) }}</strong>
                            </template>
                        </i18n-t>
                    </p>
                </div>
            </div>
        </div>
    </RouterLink>
</template>